import React from 'react';
import { Link } from 'react-router-dom';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import Zoom from '@material-ui/core/Zoom';

import { makeStyles } from '@material-ui/core/styles';
import { blue, indigo, cyan, teal, green, amber, orange, deepOrange, red, grey, blueGrey } from '@material-ui/core/colors';
import { isCoordenadorOuApoioSecretarial } from '../../security/AuthorizedFunction';

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
        margin: theme.spacing(-1),
    },
    item: {
        boxSizing: 'border-box',
        display: 'flex',
        padding: theme.spacing(1),
        width: '100%',
        [theme.breakpoints.up('sm')]: { width: '50%' },
        [theme.breakpoints.up('md')]: { width: '33.3333%' },
        [theme.breakpoints.up('lg')]: { flex: '1 1 0', width: 'auto', minWidth: 0 },
    },
    card: {
        height: '100%',
        width: '100%',
        borderLeft: '4px solid',
    },
    cardLink: {
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        transition: theme.transitions.create('box-shadow'),
        '&:hover': {
            boxShadow: theme.shadows[4],
        },
    },
    content: {
        padding: theme.spacing(2),
    },
    value: {
        fontWeight: 600,
        lineHeight: 1.2,
    },
    label: {
        marginTop: theme.spacing(0.5),
        minHeight: 38,
    },
}));

const PROVAS = '/admin/provas';

const KPI_CONFIG = [
    { key: 'recebidas', label: 'Recebidas', color: grey[600], tooltip: 'Total de Provas Recebidas/Pistoladas na OBMEP', link: `${PROVAS}` },
    { key: 'aguardandoDigitalizacao', label: 'Aguardando Digitalização', color: blueGrey[500], tooltip: 'Provas Aguardando Digitalização', link: `${PROVAS}?status=RECEBIDA,ENVIADA_DIGITALIZACAO` },
    { key: 'digitalizadas', label: 'Digitalizadas', color: blue[500], tooltip: 'Provas Digitalizadas', link: `${PROVAS}?digitalizada=true` },
    { key: 'cortadas', label: 'Cortadas', color: indigo[500], tooltip: 'Provas Cortadas', link: `${PROVAS}?cortada=true` },
    { key: 'leitura', label: 'Leitura de Marcações Realizada', color: cyan[700], tooltip: 'Provas com Leitura de Marcações Realizadas', link: `${PROVAS}?leituraMarcacoesRealizada=true` },
    { key: 'emDigitacao', label: 'Na Fila de Digitação', color: amber[700], tooltip: 'Provas em Digitação', link: `${PROVAS}?status=ENVIADA_DIGITACAO,NA_FILA_DIGITACAO` },
    { key: 'digitadas', label: 'Digitadas', color: teal[500], tooltip: 'Provas Digitadas', link: `${PROVAS}?digitada=true` },
    { key: 'emRevisao', label: 'Em Revisão pela OBMEP Central', color: orange[700], tooltip: 'Provas em Revisão', link: `${PROVAS}?status=ENVIADA_REVISAO,NA_FILA_REVISAO` },
    { key: 'revisadas', label: 'Revisadas', color: green[600], tooltip: 'Provas Revisadas' },
    { key: 'invalidadasPendentes', label: 'Invalidações Pendentes', color: deepOrange[600], tooltip: 'Invalidações de Prova Pendentes de Tratamento', link: `${PROVAS}/invalidas` },
    { key: 'comProblema', label: 'Com Problemas', color: red[600], tooltip: 'Provas com Problemas (Erros em Corte ou Leitura)', link: `${PROVAS}?comProblemas=true` },
    { key: 'descartadas', label: 'Descartadas', color: grey[500], tooltip: 'Provas Descartadas', link: `${PROVAS}?status=DESCARTADA` },
    { key: 'finalizadas', label: 'Finalizadas', color: green[800], tooltip: 'Provas Finalizadas', link: `${PROVAS}?semFinalizacao=false` },
];

const KPI_CONFIG_COORD = [
    { key: 'recebidas', label: 'Recebidas', color: grey[600], tooltip: 'Total de Provas Recebidas/Pistoladas na OBMEP', link: `${PROVAS}` },
    { key: 'aguardandoDigitalizacao', label: 'Aguardando Digitalização', color: blueGrey[500], tooltip: 'Provas Aguardando Digitalização', link: `${PROVAS}?status=RECEBIDA,ENVIADA_DIGITALIZACAO` },
    { key: 'digitalizadas', label: 'Digitalizadas', color: blue[500], tooltip: 'Provas Digitalizadas', link: `${PROVAS}?digitalizada=true` },
    { key: 'leitura', label: 'Leitura de Marcações Realizada', color: cyan[700], tooltip: 'Provas com Leitura de Marcações Realizadas', link: `${PROVAS}?leituraMarcacoesRealizada=true` },
    { key: 'emDigitacao', label: 'Na Fila de Digitação', color: amber[700], tooltip: 'Provas em Digitação', link: `${PROVAS}?status=ENVIADA_DIGITACAO,NA_FILA_DIGITACAO` },
    { key: 'digitadas', label: 'Digitadas', color: teal[500], tooltip: 'Provas Digitadas', link: `${PROVAS}?digitada=true` },
    { key: 'emRevisao', label: 'Em Revisão pela OBMEP Central', color: orange[700], tooltip: 'Provas em Revisão', link: `${PROVAS}?status=ENVIADA_REVISAO,NA_FILA_REVISAO` },
    { key: 'descartadas', label: 'Descartadas', color: grey[500], tooltip: 'Provas Descartadas', link: `${PROVAS}?status=DESCARTADA` },
    { key: 'finalizadas', label: 'Finalizadas', color: green[800], tooltip: 'Provas Finalizadas', link: `${PROVAS}?semFinalizacao=false` },
];

const formatar = valor => new Intl.NumberFormat('pt-BR').format(valor ?? 0);

const formatarPercentual = valor =>
    `${valor.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

export const Cards = ({ visaoGeral, buscarRegiaoGrupo = false }) => {
    const classes = useStyles();
    const dados = visaoGeral ?? {};

    const kpis = isCoordenadorOuApoioSecretarial() ?  KPI_CONFIG_COORD : KPI_CONFIG;

    const linkDaProva = link => {
        if (!buscarRegiaoGrupo || !link) return link;
        if (link !== PROVAS && !link.startsWith(`${PROVAS}?`)) return link;

        return `${link}${link.includes('?') ? '&' : '?'}buscarRegiaoGrupo=true`;
    };

    const recebidas = dados.recebidas ?? 0;
    const percentualFinalizadas = recebidas > 0 ? ((dados.finalizadas ?? 0) / recebidas) * 100 : null;

    return (
        <div className={classes.container}>
            {kpis.map(({ key, label, color, tooltip, link }) => (
                <div className={classes.item} key={key}>
                    <Tooltip title={tooltip} TransitionComponent={Zoom} arrow>
                        <Card
                            className={`${classes.card} ${link ? classes.cardLink : ''}`}
                            style={{ borderLeftColor: color }}
                            {...(link ? { component: Link, to: linkDaProva(link) } : {})}
                        >
                            <CardContent className={classes.content}>
                                <Typography variant="h4" className={classes.value} style={{ color }}>
                                    {formatar(dados[key])}
                                    { key === 'finalizadas' && percentualFinalizadas !== null &&
                                        <span style={{ fontSize: '0.5em', marginLeft: 6, fontWeight: 500, color: blueGrey[600] }}>
                                            ({formatarPercentual(percentualFinalizadas)})
                                        </span>
                                    }
                                </Typography>
                                <Typography variant="body2" color="textSecondary" className={classes.label}>
                                    {label}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Tooltip>
                </div>
            ))}
        </div>
    );
}

export default Cards;
